import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LineListUlIcon from 'LuxKit/Icons/line/LineListUlIcon'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import PlaceInfoDrawer from '../components/PlaceInfoDrawer'
import RightPanelEmptyState from '../components/RightPanelEmptyState'
import { MARKER_TYPE_STOP } from 'tripPlanner/utils/mapMarkerTransforms'
import {
  RightPanel,
  RightPanelHeader,
  RightPanelContent,
  EmptyStateWrapper,
} from '../styles/layoutStyles'
import type { RightPanelView } from '../hooks/useRightPanelState'
import { getTripChatCheckoutReturnView, useRightPanelState } from '../hooks/useRightPanelState'
import { useEventSubscription } from '../contexts/TripPlannerEventBus'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { MODAL_TRANSITION_DURATION } from 'LuxKit/Modal/modalConstants'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import MapContainer from './MapContainer'
import ItineraryContainer from './ItineraryContainer'
import ListContainer from './ListContainer'
import CartPanelContainer from './CartPanelContainer'
import { buildTripListRows } from '../components/ListView/tripListRows'
import { getRightPanelRenderState } from '../utils/rightPanelRenderState'

function RightPanelContainer() {
  const intl = useIntl()
  const { sendMessage } = useChatActions()
  const { itineraryData, hasItineraryData } = useItineraryContext()

  const {
    rightPanelView,
    setRightPanelView,
    rememberRightPanelView,
    selectedDayItineraryId,
    setSelectedDayItineraryId,
    selectedMarkerData,
    handleMarkerSelect,
    handleMarkerOverlayClose,
  } = useRightPanelState()

  // ── Computed state ──

  const hasTripListRows = useMemo(() => {
    return buildTripListRows(itineraryData, intl).length > 0
  }, [intl, itineraryData])

  const selectedStop = useMemo(() => {
    if (!selectedDayItineraryId || !itineraryData) {
      return undefined
    }
    // Find the stop that contains this day
    for (const stop of itineraryData.stops) {
      const dayExists = stop.days.some((day: { id: number }) => day.id === selectedDayItineraryId)
      if (dayExists) {
        return stop
      }
    }
    return undefined
  }, [selectedDayItineraryId, itineraryData])

  const {
    shouldShowMap,
    shouldShowTimelineEmptyState,
    shouldShowTripPlanCanvas,
    shouldShowListEmptyState,
    shouldShowListView,
  } = getRightPanelRenderState({
    rightPanelView,
    hasItineraryData,
    hasTripListRows,
  })

  // ── Cart panel animation state (TripDrawer pattern) ──

  const isCartView = rightPanelView === 'cart'
  const [shouldRenderCart, setShouldRenderCart] = useState(isCartView)
  const [isCartClosing, setIsCartClosing] = useState(false)
  const preCartViewRef = useRef<Exclude<RightPanelView, 'cart'>>('map')

  useEffect(() => {
    if (isCartView) {
      setShouldRenderCart(true)
      setIsCartClosing(false)
    } else if (shouldRenderCart) {
      setIsCartClosing(true)
      const timer = setTimeout(() => {
        setShouldRenderCart(false)
        setIsCartClosing(false)
      }, MODAL_TRANSITION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [isCartView, shouldRenderCart])

  const hideToggle = shouldRenderCart

  // ── View toggle ──

  const handleViewToggleWithCleanup = useCallback((view: RightPanelView) => {
    rememberRightPanelView(view)
    setRightPanelView(view)
  }, [rememberRightPanelView, setRightPanelView])

  const handleCartClose = useCallback(() => {
    setRightPanelView(preCartViewRef.current)
  }, [setRightPanelView])

  // ── Event bus subscriptions ──

  useEventSubscription('open_cart_panel', () => {
    setRightPanelView((prev) => {
      if (prev === 'cart') return preCartViewRef.current
      preCartViewRef.current = prev
      return 'cart'
    })
  })

  useEventSubscription('reset_chat_state', () => {
    setRightPanelView(getTripChatCheckoutReturnView() ?? 'map')
    setSelectedDayItineraryId(undefined)
    handleMarkerOverlayClose()
  })

  // ── i18n ──

  const timelineOptionTitle = intl.formatMessage({
    id: 'CrXe4aeBNf4hdDEigM3u',
    defaultMessage: 'Timeline',
    description: 'Segmented option title for the timeline view in the trip planner right panel',
  })

  const listOptionTitle = intl.formatMessage({
    id: 'mgKCGdrsVCcLCxmhhoN-',
    defaultMessage: 'List',
    description: 'Segmented option title for the list view in the trip planner right panel',
  })

  const mapOptionTitle = intl.formatMessage({
    id: '3uJcGV6ZPs3TPDucD-0n',
    defaultMessage: 'Map',
    description: 'Segmented option title for the map view in the trip planner right panel',
  })

  return (
    <RightPanel>
      {!hideToggle && (
        <RightPanelHeader>
          <SegmentedOptionsGroup
            inputsName="rightPanelView"
            onValueChange={handleViewToggleWithCleanup}
          >
            <SegmentedOption
              value="timeline"
              title={timelineOptionTitle}
              icon={<LineClockIcon size="XS" />}
              checked={rightPanelView === 'timeline'}
            />
            <SegmentedOption
              value="list"
              title={listOptionTitle}
              icon={<LineListUlIcon size="XS" />}
              checked={rightPanelView === 'list'}
            />
            <SegmentedOption
              value="map"
              title={mapOptionTitle}
              icon={<LineMapIcon size="XS" />}
              checked={rightPanelView === 'map'}
            />
          </SegmentedOptionsGroup>
        </RightPanelHeader>
      )}
      <RightPanelContent>
        <ErrorBoundary fallback={null}>
          <MapContainer
            visible={shouldShowMap}
            selectedDayItineraryId={selectedDayItineraryId}
            selectedStop={selectedStop}
            selectedMarkerData={selectedMarkerData}
            onMarkerSelect={handleMarkerSelect}
            setRightPanelView={setRightPanelView}
            rightPanelView={rightPanelView}
          />
        </ErrorBoundary>
        {shouldShowTimelineEmptyState && (
          <EmptyStateWrapper padding={40}>
            <RightPanelEmptyState variant="timeline" />
          </EmptyStateWrapper>
        )}
        <ItineraryContainer
          visible={shouldShowTripPlanCanvas}
          selectedDayItineraryId={selectedDayItineraryId}
          selectedStop={selectedStop}
        />
        {shouldShowListEmptyState && (
          <EmptyStateWrapper padding={40}>
            <RightPanelEmptyState variant="list" />
          </EmptyStateWrapper>
        )}
        <ListContainer visible={shouldShowListView} />
      </RightPanelContent>
      {shouldRenderCart && (
        <CartPanelContainer
          isClosing={isCartClosing}
          onClose={handleCartClose}
        />
      )}
      {selectedMarkerData?.marker?.placeId && (
        <PlaceInfoDrawer
          isOpen={Boolean(selectedMarkerData)}
          onClose={handleMarkerOverlayClose}
          googlePlaceId={selectedMarkerData.marker.placeId}
          placeName={selectedMarkerData.marker.title || selectedMarkerData.marker.label || ''}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          hideAddToTrip={selectedMarkerData.marker.markerType === MARKER_TYPE_STOP}
        />
      )}
    </RightPanel>
  )
}

export default RightPanelContainer
